import type { LocationTypeData, PlannerRegion, RigData, SpeciesData, TechniqueData } from '../../types';
import { inferSpeciesTargetability } from '../../utils/speciesTargetability';
import {
  fallbackRecommendationCatalog,
  isTechniqueManualReviewHold,
  resolveRecommendationCatalog,
  type RecommendationAccessType,
  type RecommendationBudgetBand,
  type RecommendationCatalogBundle,
  type ResolvedRecommendationCatalog
} from '../recommendations/catalog';
import {
  DEFAULT_PLANNER_REGION,
  PLANNER_MONTHS,
  formatPlannerRegionLabel,
  getActiveMonths,
  parseSeasonality,
  scoreSeasonStatus,
  type SeasonalityWindow
} from './seasonality';
import {
  getBeginnerAnchorFlags,
  getBeginnerAnchorLabel
} from './beginnerAnchors';

export type PlannerExperienceLevel = 'beginner' | 'intermediate';

export interface PlannerLensInput {
  monthIndex: number;
  region: PlannerRegion;
  accessType: RecommendationAccessType;
  budgetBand: RecommendationBudgetBand;
}

export interface PlannerSpeciesScore {
  score: number;
  reason: string;
  targetability: ReturnType<typeof inferSpeciesTargetability>;
}

export interface PlannerTechniqueScore {
  score: number;
  reason: string;
}

export interface PlannerRigScore {
  score: number;
  reason: string;
}

export interface PlannerLocationScore {
  score: number;
  reason: string;
}

export interface PlannerOpportunity {
  id: string;
  score: number;
  species: SpeciesData;
  technique: TechniqueData;
  rig: RigData | null;
  location: LocationTypeData | null;
  speciesWindow: SeasonalityWindow;
  techniqueWindow: SeasonalityWindow;
  rigWindow: SeasonalityWindow;
  locationWindow: SeasonalityWindow;
  combinedMonths: number[];
  reasonBullets: string[];
  trustLabel: string;
  trustTone: 'reviewed' | 'learning' | 'fallback';
  bestWindowLabel: string;
  catalog: ResolvedRecommendationCatalog;
  beginnerAnchorMatches: number;
  beginnerAnchorLabel: string | null;
  beginnerAnchorFlags: ReturnType<typeof getBeginnerAnchorFlags>;
}

export interface PlannerOpportunityResolution {
  opportunities: PlannerOpportunity[];
  effectiveLens: PlannerLensInput;
  fallbackApplied: boolean;
}

const difficultyRank: Record<'beginner' | 'intermediate' | 'advanced', number> = {
  beginner: 0,
  intermediate: 1,
  advanced: 2
};

export const inferPlannerSpeciesFamily = (species: SpeciesData): string => {
  const haystack = `${species.name} ${species.scientificName} ${species.speciesId}`.toLowerCase();

  if (/diplodus|sparus|pagrus|bream|porgy|steenbras/.test(haystack)) {
    return 'sea_bream';
  }

  if (/dicentrarchus|sea bass/.test(haystack)) {
    return 'sea_bass';
  }

  if (/thunnus|tuna|bonito|amberjack|mackerel|seriola/.test(haystack)) {
    return 'pelagic';
  }

  if (/octopus|squid|cuttlefish|sepia|loligo/.test(haystack)) {
    return 'cephalopod';
  }

  if (/scorpionfish|hake|dentex|dory|grouper|conger|sole|plaice|forkbeard/.test(haystack)) {
    return 'demersal';
  }

  return 'generalist';
};

export const isTechniqueAccessMatch = (technique: TechniqueData, accessType: RecommendationAccessType): boolean => {
  const haystack = `${technique.name} ${technique.category} ${technique.techniqueId}`.toLowerCase();

  if (accessType === 'both') {
    return true;
  }

  if (accessType === 'shore') {
    return /(shore|bottom|float|surf|rock|bolognese|spinning|sabiki|squid|egi|cephalopod|harbor)/.test(haystack);
  }

  return /(boat|offshore|deep|trolling|drift|jig|downrigger|lampuga|bottom|sabiki)/.test(haystack);
};

export const isLocationAccessMatch = (location: LocationTypeData, accessType: RecommendationAccessType): boolean => {
  if (accessType === 'both') {
    return true;
  }

  if (accessType === 'shore') {
    return ['coastal', 'harbor', 'estuary'].includes(location.category);
  }

  return ['offshore', 'harbor', 'coastal'].includes(location.category);
};

export const isRigAccessMatch = (rig: RigData, accessType: RecommendationAccessType): boolean => {
  if (accessType === 'both') {
    return true;
  }

  if (accessType === 'shore') {
    return ['bottom', 'float', 'surf', 'spinning', 'specialized'].includes(rig.category);
  }

  return ['trolling', 'live_bait', 'specialized', 'bottom'].includes(rig.category);
};

const uniqueMonths = (months: number[]): number[] => Array.from(new Set(months)).sort((left, right) => left - right);

const buildCombinedMonths = (
  speciesWindow: SeasonalityWindow,
  techniqueWindow: SeasonalityWindow,
  locationWindow: SeasonalityWindow,
  rigWindow: SeasonalityWindow
): number[] => {
  if (speciesWindow.yearRound || techniqueWindow.yearRound || locationWindow.yearRound || rigWindow.yearRound) {
    return PLANNER_MONTHS.map((month) => month.index);
  }

  return uniqueMonths([
    ...getActiveMonths(speciesWindow),
    ...getActiveMonths(techniqueWindow),
    ...getActiveMonths(locationWindow),
    ...getActiveMonths(rigWindow)
  ]);
};

const getTrustState = (catalog: ResolvedRecommendationCatalog): { label: string; tone: PlannerOpportunity['trustTone'] } => {
  if (catalog.sourceType === 'curated_catalog' && catalog.bundle.commerceEnabled && catalog.bundle.reviewStatus === 'curated') {
    return { label: 'Ready', tone: 'reviewed' };
  }

  if (!catalog.bundle.commerceEnabled || catalog.sourceType === 'manual_review_hold') {
    return { label: 'Learn', tone: 'learning' };
  }

  return { label: 'Explore', tone: 'fallback' };
};

const buildBestWindowLabel = (location: LocationTypeData | null, monthIndex: number): string => {
  if (!location) {
    return `${PLANNER_MONTHS[monthIndex].label} sessions`;
  }

  const times = location.bestTimes.timeOfDay?.slice(0, 2).join(' + ');
  if (times && location.bestTimes.tideConditions) {
    return `${times} • ${location.bestTimes.tideConditions}`;
  }

  if (times) {
    return times;
  }

  if (location.bestTimes.tideConditions) {
    return location.bestTimes.tideConditions;
  }

  return location.bestTimes.season || `${PLANNER_MONTHS[monthIndex].label} sessions`;
};

const buildReasonBullets = (
  species: SpeciesData,
  technique: TechniqueData,
  rig: RigData | null,
  location: LocationTypeData | null,
  speciesWindow: SeasonalityWindow,
  techniqueWindow: SeasonalityWindow,
  locationWindow: SeasonalityWindow,
  rigWindow: SeasonalityWindow,
  monthIndex: number,
  region: PlannerRegion,
  trustLabel: string,
  accessType: RecommendationAccessType
): string[] => {
  const reasons = [
    speciesWindow.months.length > 0 || speciesWindow.yearRound
      ? `${species.name} lines up with ${PLANNER_MONTHS[monthIndex].label.toLowerCase()} in the ${formatPlannerRegionLabel(region).toLowerCase()} via ${speciesWindow.summary}.`
      : `${species.name} still needs tighter month tagging, so the route leans on reviewed technique and spot fit instead.`,
    techniqueWindow.months.length > 0 || techniqueWindow.yearRound
      ? `${technique.name} is reviewed for ${formatPlannerRegionLabel(region)} and stays strongest in ${techniqueWindow.summary}.`
      : `${technique.name} is still the clearest ${accessType === 'boat' ? 'boat' : accessType === 'shore' ? 'shore' : 'Mediterranean'} path for this fish right now.`
  ];

  if (location) {
    reasons.push(
      locationWindow.months.length > 0 || locationWindow.yearRound
        ? `${location.name} is marked best in ${locationWindow.summary}.`
        : `${location.name} is still a credible starter spot even though its seasonality is broad.`
    );
  }

  if (rig) {
    reasons.push(
      rigWindow.months.length > 0 || rigWindow.yearRound
        ? `${rig.name} stays aligned with ${rigWindow.summary}.`
        : `${rig.name} is the simplest rig that matches this route.`
    );
  }

  const beginnerAnchorFlags = getBeginnerAnchorFlags(species, technique, rig, location);
  const beginnerAnchorLabel = getBeginnerAnchorLabel(beginnerAnchorFlags);

  if (beginnerAnchorLabel) {
    reasons.push(`${beginnerAnchorLabel} keeps the first route anchored to the reviewed fish, method, and spot chain.`);
  } else {
    reasons.push(`${trustLabel} keeps the gear side honest before it becomes a sales surface.`);
  }

  return reasons;
};

export const scoreSpeciesForPlannerLens = (species: SpeciesData, lens: PlannerLensInput): PlannerSpeciesScore => {
  const targetability = inferSpeciesTargetability(species);
  const speciesWindow = parseSeasonality(species.seasonality || species.fishingSeason);
  const seasonScore = scoreSeasonStatus(speciesWindow, lens.monthIndex, lens.region, {
    in_season: 36,
    shoulder: 18,
    out_of_season: -38,
    region_unverified: -18,
    unknown: 8
  });

  let score = seasonScore;
  if (targetability === 'targetable') {
    score += 12;
  } else if (targetability === 'incidental') {
    score -= 18;
  } else {
    score -= 60;
  }

  const accessBonus = species.fishingTechniqueIds.some((techniqueId) => {
    if (lens.accessType === 'shore') {
      return ['bottom_fishing', 'float_fishing', 'bolognese_fishing', 'spinning_shore', 'surfcasting', 'squid_jigging'].includes(techniqueId);
    }

    if (lens.accessType === 'boat') {
      return ['bottom_fishing', 'trolling_simple', 'drift_fishing', 'mediterranean_drifting', 'lampuga_fishing'].includes(techniqueId);
    }

    return true;
  }) ? 8 : 0;

  score += accessBonus + Math.min(species.fishingTechniqueIds.length, 5);

  const reason = speciesWindow.months.length > 0 || speciesWindow.yearRound
    ? `${species.name} is ${seasonScore >= 30 ? 'strongest' : seasonScore >= 12 ? 'still credible' : 'more tentative'} in ${PLANNER_MONTHS[lens.monthIndex].label} for the ${formatPlannerRegionLabel(lens.region)} via ${speciesWindow.summary}.`
    : `${species.name} stays visible because the codex links it to a usable fishing chain, even though its month tagging is still broad.`;

  return {
    score,
    reason,
    targetability
  };
};

const getRigBudgetAdjustment = (rig: RigData, budgetBand: RecommendationBudgetBand): number => {
  if (budgetBand === 'premium') {
    return rig.category === 'specialized' || rig.category === 'trolling' ? 6 : 3;
  }

  if (budgetBand === 'budget') {
    if (rig.category === 'trolling' || rig.category === 'live_bait') {
      return -12;
    }

    if (rig.category === 'specialized') {
      return -6;
    }

    return 4;
  }

  return rig.category === 'trolling' ? -4 : 2;
};

const getLocationCategoryFit = (location: LocationTypeData, accessType: RecommendationAccessType): number => {
  if (accessType === 'both') {
    return ['harbor', 'coastal', 'structure'].includes(location.category) ? 6 : 3;
  }

  if (accessType === 'shore') {
    if (['coastal', 'harbor', 'estuary', 'structure'].includes(location.category)) {
      return 12;
    }

    return -18;
  }

  if (location.category === 'offshore') {
    return 12;
  }

  if (['harbor', 'structure', 'coastal'].includes(location.category)) {
    return 4;
  }

  return -14;
};

export const scoreTechniqueForPlannerLens = (technique: TechniqueData, lens: PlannerLensInput): PlannerTechniqueScore => {
  const techniqueWindow = parseSeasonality(technique.seasonality);
  const seasonScore = scoreSeasonStatus(techniqueWindow, lens.monthIndex, lens.region, {
    in_season: 28,
    shoulder: 14,
    out_of_season: -28,
    region_unverified: -12,
    unknown: 5
  });
  const accessScore = isTechniqueAccessMatch(technique, lens.accessType) ? 12 : -18;
  const difficultyScore = technique.difficulty === 'beginner' ? 12 : technique.difficulty === 'intermediate' ? 6 : -8;
  const holdPenalty = isTechniqueManualReviewHold(technique.techniqueId) ? -6 : 0;
  const score = seasonScore + accessScore + difficultyScore + holdPenalty;

  const reason = techniqueWindow.months.length > 0 || techniqueWindow.yearRound
    ? `${technique.name} lines up with ${PLANNER_MONTHS[lens.monthIndex].label.toLowerCase()} in the ${formatPlannerRegionLabel(lens.region).toLowerCase()} and stays ${isTechniqueAccessMatch(technique, lens.accessType) ? 'aligned' : 'less aligned'} with a ${lens.accessType === 'both' ? 'mixed-access' : lens.accessType} plan.`
    : `${technique.name} stays ranked mainly on access fit and its role in the reviewed codex chain.`;

  return {
    score,
    reason
  };
};

export const scoreRigForPlannerLens = (rig: RigData, lens: PlannerLensInput): PlannerRigScore => {
  const rigWindow = parseSeasonality(rig.seasonality || rig.applications.season);
  const seasonScore = scoreSeasonStatus(rigWindow, lens.monthIndex, lens.region, {
    in_season: 24,
    shoulder: 12,
    out_of_season: -24,
    region_unverified: -10,
    unknown: 4
  });
  const accessScore = isRigAccessMatch(rig, lens.accessType) ? 12 : -18;
  const difficultyScore = rig.difficulty === 'beginner' ? 12 : rig.difficulty === 'intermediate' ? 6 : -8;
  const budgetScore = getRigBudgetAdjustment(rig, lens.budgetBand);
  const score = seasonScore + accessScore + difficultyScore + budgetScore + Math.min(rig.targetSpeciesIds.length, 6);

  const reason = rigWindow.months.length > 0 || rigWindow.yearRound
    ? `${rig.name} fits ${PLANNER_MONTHS[lens.monthIndex].label.toLowerCase()} in the ${formatPlannerRegionLabel(lens.region).toLowerCase()} and stays ${isRigAccessMatch(rig, lens.accessType) ? 'aligned' : 'less aligned'} with a ${lens.accessType === 'both' ? 'mixed-access' : lens.accessType} setup.`
    : `${rig.name} is being ranked on access fit, difficulty, and how practical it is for a ${lens.budgetBand} ${lens.accessType === 'both' ? 'Mediterranean' : lens.accessType} plan.`;

  return {
    score,
    reason
  };
};

export const scoreLocationForPlannerLens = (location: LocationTypeData, lens: PlannerLensInput): PlannerLocationScore => {
  const locationWindow = parseSeasonality(location.seasonality || location.bestTimes.season);
  const seasonScore = scoreSeasonStatus(locationWindow, lens.monthIndex, lens.region, {
    in_season: 26,
    shoulder: 14,
    out_of_season: -22,
    region_unverified: -10,
    unknown: 5
  });
  const accessScore = getLocationCategoryFit(location, lens.accessType);
  const accessibilityScore =
    location.accessibility === 'easy' ? 12 :
    location.accessibility === 'moderate' ? 5 :
    lens.accessType === 'boat' ? -2 : -10;
  const score = seasonScore + accessScore + accessibilityScore + Math.min(location.commonSpeciesIds.length, 6) + Math.min(location.recommendedTechniqueIds.length, 4);

  const reason = locationWindow.months.length > 0 || locationWindow.yearRound
    ? `${location.name} fits ${PLANNER_MONTHS[lens.monthIndex].label.toLowerCase()} in the ${formatPlannerRegionLabel(lens.region).toLowerCase()} and stays ${location.accessibility} to approach for a ${lens.accessType === 'both' ? 'mixed-access' : lens.accessType} route.`
    : `${location.name} is being ranked on access, accessibility, and how well it supports this route right now.`;

  return {
    score,
    reason
  };
};


const diversifyPlannerOpportunities = (opportunities: PlannerOpportunity[], limit: number): PlannerOpportunity[] => {
  const pool = [...opportunities].sort((left, right) => right.score - left.score);
  const selected: PlannerOpportunity[] = [];
  const usedTechniques = new Map<string, number>();
  const usedTechniqueCategories = new Map<string, number>();
  const usedFamilies = new Map<string, number>();
  const usedLocations = new Map<string, number>();
  const usedRigCategories = new Map<string, number>();
  const usedCatalogEntries = new Map<string, number>();

  while (pool.length > 0 && selected.length < limit) {
    let bestIndex = 0;
    let bestScore = Number.NEGATIVE_INFINITY;

    pool.forEach((opportunity, index) => {
      const family = inferPlannerSpeciesFamily(opportunity.species);
      const locationKey = opportunity.location?.category || 'unknown';
      const techniqueCategoryKey = opportunity.technique.category || opportunity.technique.techniqueId;
      const rigCategoryKey = opportunity.rig?.category || 'none';
      const catalogKey = opportunity.catalog.bundle.id || 'fallback';
      const techniquePenalty = (usedTechniques.get(opportunity.technique.techniqueId) || 0) * 16;
      const techniqueCategoryPenalty = (usedTechniqueCategories.get(techniqueCategoryKey) || 0) * 9;
      const familyPenalty = (usedFamilies.get(family) || 0) * 8;
      const locationPenalty = (usedLocations.get(locationKey) || 0) * 6;
      const rigPenalty = (usedRigCategories.get(rigCategoryKey) || 0) * 7;
      const catalogPenalty = (usedCatalogEntries.get(catalogKey) || 0) * 11;
      const reviewedBoost = opportunity.trustTone === 'reviewed' ? 6 : 0;
      const freshBundleBoost = opportunity.catalog.sourceType === 'curated_catalog' && !usedCatalogEntries.has(catalogKey) ? 4 : 0;
      const adjustedScore = opportunity.score - techniquePenalty - techniqueCategoryPenalty - familyPenalty - locationPenalty - rigPenalty - catalogPenalty + reviewedBoost + freshBundleBoost;

      if (adjustedScore > bestScore) {
        bestScore = adjustedScore;
        bestIndex = index;
      }
    });

    const [next] = pool.splice(bestIndex, 1);
    selected.push(next);
    const family = inferPlannerSpeciesFamily(next.species);
    const locationKey = next.location?.category || 'unknown';
    const techniqueCategoryKey = next.technique.category || next.technique.techniqueId;
    const rigCategoryKey = next.rig?.category || 'none';
    const catalogKey = next.catalog.bundle.id || 'fallback';
    usedTechniques.set(next.technique.techniqueId, (usedTechniques.get(next.technique.techniqueId) || 0) + 1);
    usedTechniqueCategories.set(techniqueCategoryKey, (usedTechniqueCategories.get(techniqueCategoryKey) || 0) + 1);
    usedFamilies.set(family, (usedFamilies.get(family) || 0) + 1);
    usedLocations.set(locationKey, (usedLocations.get(locationKey) || 0) + 1);
    usedRigCategories.set(rigCategoryKey, (usedRigCategories.get(rigCategoryKey) || 0) + 1);
    usedCatalogEntries.set(catalogKey, (usedCatalogEntries.get(catalogKey) || 0) + 1);
  }

  return selected;
};

const buildPlannerOpportunitiesExact = (
  species: SpeciesData[],
  techniques: TechniqueData[],
  rigs: RigData[],
  locations: LocationTypeData[],
  catalogEntries: RecommendationCatalogBundle[] = fallbackRecommendationCatalog,
  lens: PlannerLensInput,
  experienceLevel: PlannerExperienceLevel = 'beginner',
  limit = 8
): PlannerOpportunity[] => {
  const opportunities: PlannerOpportunity[] = [];

  species
    .filter((item) => inferSpeciesTargetability(item) === 'targetable')
    .forEach((item) => {
      const speciesWindow = parseSeasonality(item.seasonality || item.fishingSeason);
      const speciesSeasonScore = scoreSeasonStatus(speciesWindow, lens.monthIndex, lens.region, {
        in_season: 35,
        shoulder: 18,
        out_of_season: -35,
        region_unverified: -22,
        unknown: 8
      });
      if (speciesSeasonScore < 0) {
        return;
      }

      const techniqueCandidates = techniques
        .filter((technique) => {
          const isLinked = item.fishingTechniqueIds.includes(technique.techniqueId) || technique.targetSpeciesIds?.includes(item.speciesId);
          if (!isLinked) {
            return false;
          }

          if (!isTechniqueAccessMatch(technique, lens.accessType)) {
            return false;
          }

          return difficultyRank[technique.difficulty] <= (experienceLevel === 'beginner' ? difficultyRank.intermediate : difficultyRank.advanced);
        })
        .map((technique) => {
          const techniqueWindow = parseSeasonality(technique.seasonality);
          const techniqueSeasonScore = scoreSeasonStatus(techniqueWindow, lens.monthIndex, lens.region, {
            in_season: 14,
            shoulder: 8,
            out_of_season: -14,
            region_unverified: -8,
            unknown: 2
          });
          const locationCandidates = locations
            .filter((location) => {
              const speciesMatch = location.commonSpeciesIds.includes(item.speciesId);
              const techniqueMatch = location.recommendedTechniqueIds.includes(technique.techniqueId);
              if (!speciesMatch && !techniqueMatch) {
                return false;
              }

              return isLocationAccessMatch(location, lens.accessType);
            })
            .map((location) => {
              const locationWindow = parseSeasonality(location.seasonality || location.bestTimes.season);
              const locationAnchorBoost = (item.primaryBeginnerLocationTypeId === location.locationTypeId ? 26 : 0) + (location.primaryBeginnerTechniqueId === technique.techniqueId ? 14 : 0);
              return {
                location,
                window: locationWindow,
                score: scoreSeasonStatus(locationWindow, lens.monthIndex, lens.region, {
                  in_season: 14,
                  shoulder: 6,
                  out_of_season: -12,
                  region_unverified: -8,
                  unknown: 2
                }) + (location.recommendedTechniqueIds.includes(technique.techniqueId) ? 10 : 4) + locationAnchorBoost
              };
            })
            .sort((left, right) => right.score - left.score);

          const rigCandidates = rigs
            .filter((rig) => {
              const techniqueMatch = rig.suitableTechniqueIds.includes(technique.techniqueId);
              const speciesMatch = rig.targetSpeciesIds.includes(item.speciesId);
              if (!techniqueMatch && !speciesMatch) {
                return false;
              }

              if (!isRigAccessMatch(rig, lens.accessType)) {
                return false;
              }

              return difficultyRank[rig.difficulty] <= (experienceLevel === 'beginner' ? difficultyRank.intermediate : difficultyRank.advanced);
            })
            .map((rig) => {
              const rigWindow = parseSeasonality(rig.seasonality || rig.applications.season);
              const rigAnchorBoost = technique.primaryBeginnerRigId === rig.rigId ? 24 : 0;
              return {
                rig,
                window: rigWindow,
                score: scoreSeasonStatus(rigWindow, lens.monthIndex, lens.region, {
                  in_season: 10,
                  shoulder: 5,
                  out_of_season: -10,
                  region_unverified: -6,
                  unknown: 1
                }) + (rig.suitableTechniqueIds.includes(technique.techniqueId) ? 8 : 4) + rigAnchorBoost
              };
            })
            .sort((left, right) => right.score - left.score);

          const topLocation = locationCandidates[0] || null;
          const topRig = rigCandidates[0] || null;
          const resolvedCatalog = resolveRecommendationCatalog({
            placement: 'planner_featured',
            species: item,
            technique,
            rig: topRig?.rig || null,
            budgetBand: lens.budgetBand,
            accessType: lens.accessType
          }, catalogEntries);
          const speciesFamily = inferPlannerSpeciesFamily(item);
          const hasDirectSpeciesMatch = Boolean(resolvedCatalog.bundle.match.speciesIds?.includes(item.speciesId));
          const hasFamilyMatch = Boolean(resolvedCatalog.bundle.match.speciesFamilies?.includes(speciesFamily as never));
          const catalogScore = resolvedCatalog.sourceType === 'curated_catalog'
            ? resolvedCatalog.bundle.commerceEnabled
              ? hasDirectSpeciesMatch
                ? 26
                : hasFamilyMatch
                  ? 22
                  : 10
              : 8
            : resolvedCatalog.sourceType === 'manual_review_hold'
              ? -6
              : hasDirectSpeciesMatch || hasFamilyMatch
                ? 2
                : -10;
          const learningHoldPenalty = isTechniqueManualReviewHold(technique.techniqueId) ? -4 : 0;

          return {
            technique,
            location: topLocation?.location || null,
            locationWindow: topLocation?.window || parseSeasonality(null),
            rig: topRig?.rig || null,
            techniqueWindow,
            rigWindow: topRig?.window || parseSeasonality(null),
            score:
              speciesSeasonScore +
              techniqueSeasonScore +
              catalogScore +
              learningHoldPenalty +
              (item.primaryBeginnerTechniqueId === technique.techniqueId ? 34 : 0) +
              (difficultyRank[technique.difficulty] === 0 ? 14 : 8) +
              (topLocation?.score || 0) +
              (topRig?.score || 0)
          };
        })
        .sort((left, right) => right.score - left.score);

      const topRoute = techniqueCandidates[0];
      if (!topRoute) {
        return;
      }

      const beginnerAnchorFlags = getBeginnerAnchorFlags(item, topRoute.technique, topRoute.rig, topRoute.location);
      const beginnerAnchorLabel = getBeginnerAnchorLabel(beginnerAnchorFlags);
      const beginnerAnchorMatches = Object.values(beginnerAnchorFlags).filter(Boolean).length;
      const catalog = resolveRecommendationCatalog({
        placement: 'planner_featured',
        species: item,
        technique: topRoute.technique,
        rig: topRoute.rig,
        budgetBand: lens.budgetBand,
        accessType: lens.accessType
      }, catalogEntries);
      const trustState = getTrustState(catalog);
      const combinedMonths = buildCombinedMonths(speciesWindow, topRoute.techniqueWindow, topRoute.locationWindow, topRoute.rigWindow);

      opportunities.push({
        id: `${item.speciesId}:${topRoute.technique.techniqueId}:${topRoute.rig?.rigId || 'none'}:${topRoute.location?.locationTypeId || 'none'}`,
        score: topRoute.score,
        species: item,
        technique: topRoute.technique,
        rig: topRoute.rig,
        location: topRoute.location,
        speciesWindow,
        techniqueWindow: topRoute.techniqueWindow,
        rigWindow: topRoute.rigWindow,
        locationWindow: topRoute.locationWindow,
        combinedMonths,
        reasonBullets: buildReasonBullets(
          item,
          topRoute.technique,
          topRoute.rig,
          topRoute.location,
          speciesWindow,
          topRoute.techniqueWindow,
          topRoute.locationWindow,
          topRoute.rigWindow,
          lens.monthIndex,
          lens.region,
          trustState.label,
          lens.accessType
        ),
        trustLabel: trustState.label,
        trustTone: trustState.tone,
        bestWindowLabel: buildBestWindowLabel(topRoute.location, lens.monthIndex),
        catalog,
        beginnerAnchorMatches,
        beginnerAnchorLabel,
        beginnerAnchorFlags
      });
    });

  return diversifyPlannerOpportunities(opportunities, limit);
};

const dedupePlannerAttempts = (attempts: PlannerLensInput[]): PlannerLensInput[] => {
  const seen = new Set();
  return attempts.filter((attempt) => {
    const key = [attempt.monthIndex, attempt.region, attempt.accessType, attempt.budgetBand].join(':');
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const resolvePlannerOpportunities = (
  species: SpeciesData[],
  techniques: TechniqueData[],
  rigs: RigData[],
  locations: LocationTypeData[],
  catalogEntries: RecommendationCatalogBundle[] = fallbackRecommendationCatalog,
  lens: PlannerLensInput,
  experienceLevel: PlannerExperienceLevel = 'beginner',
  limit = 8
): PlannerOpportunityResolution => {
  const attempts = dedupePlannerAttempts([
    lens,
    { ...lens, accessType: 'both' },
    { ...lens, accessType: 'both', budgetBand: lens.budgetBand === 'premium' ? 'mid' : lens.budgetBand },
    { ...lens, region: DEFAULT_PLANNER_REGION, accessType: 'both', budgetBand: 'mid' }
  ]);

  for (let index = 0; index < attempts.length; index += 1) {
    const attempt = attempts[index];
    const opportunities = buildPlannerOpportunitiesExact(species, techniques, rigs, locations, catalogEntries, attempt, experienceLevel, limit);
    if (opportunities.length > 0) {
      return {
        opportunities,
        effectiveLens: attempt,
        fallbackApplied: index > 0
      };
    }
  }

  return {
    opportunities: [],
    effectiveLens: lens,
    fallbackApplied: false
  };
};

export const buildPlannerOpportunities = (
  species: SpeciesData[],
  techniques: TechniqueData[],
  rigs: RigData[],
  locations: LocationTypeData[],
  catalogEntries: RecommendationCatalogBundle[] = fallbackRecommendationCatalog,
  lens: PlannerLensInput,
  experienceLevel: PlannerExperienceLevel = 'beginner',
  limit = 8
): PlannerOpportunity[] => resolvePlannerOpportunities(species, techniques, rigs, locations, catalogEntries, lens, experienceLevel, limit).opportunities;
